import { Component, inject, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DashboardNotificationsService, DashNotification } from '../../../core/services/dashboard-notifications.service';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule, MatMenuModule, MatBadgeModule, MatTooltipModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button mat-icon-button
      [matMenuTriggerFor]="notifMenu"
      (menuOpened)="onOpen()"
      class="bell-btn"
      [matTooltip]="'notifications.title' | translate">
      <mat-icon
        [matBadge]="unreadCount > 0 ? (unreadCount > 9 ? '9+' : unreadCount.toString()) : null"
        matBadgeColor="warn"
        matBadgeSize="small">
        notifications
      </mat-icon>
    </button>

    <mat-menu #notifMenu="matMenu" class="notif-menu" xPosition="after">
      <div class="notif-header" (click)="$event.stopPropagation()">
        <div class="notif-header-left">
          <span class="notif-title">{{ 'notifications.title' | translate }}</span>
          @if (unreadCount > 0) {
          <span class="notif-count-badge">{{ unreadCount }}</span>
          }
        </div>
        @if (notifications.length > 0) {
        <button mat-button class="mark-read-btn" (click)="clearAll()">
          {{ 'notifications.clear_all' | translate }}
        </button>
        }
      </div>

      @if (notifications.length === 0) {
      <div class="notif-empty" (click)="$event.stopPropagation()">
        <div class="notif-empty-icon">
          <mat-icon>notifications_none</mat-icon>
        </div>
        <p class="notif-empty-title">{{ 'notifications.empty' | translate }}</p>
        <p class="notif-empty-sub">{{ 'notifications.empty_sub' | translate }}</p>
      </div>
      } @else {
      <div class="notif-list" (click)="$event.stopPropagation()">
        @for (n of notifications.slice(0, 10); track n.id) {
        <div class="notif-item" [class.unread]="!n.readAt" (click)="navigate(n)">
          <div class="notif-icon" [class]="iconClass(n.type)">
            <mat-icon>{{ iconName(n.type) }}</mat-icon>
          </div>
          <div class="notif-content">
            <div class="notif-item-title">{{ n.title }}</div>
            <div class="notif-item-body">{{ n.body }}</div>
            <div class="notif-item-time">{{ timeAgo(n.createdAt) }}</div>
          </div>
          @if (!n.readAt) {
          <div class="unread-dot"></div>
          }
        </div>
        }
      </div>
      }
    </mat-menu>
  `,
  styles: [`
    .bell-btn { color: inherit; }
  `]
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private svc = inject(DashboardNotificationsService);
  private cdr = inject(ChangeDetectorRef);
  private sub?: Subscription;

  notifications: DashNotification[] = [];
  unreadCount = 0;

  ngOnInit(): void {
    this.sub = this.svc.notifications$.subscribe(list => {
      this.notifications = list;
      this.unreadCount = list.filter(n => !n.readAt).length;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onOpen(): void {
    // mark all read after a short delay so user can see which are new
    setTimeout(() => this.svc.markAllRead(), 2000);
  }

  navigate(n: DashNotification): void {
    this.svc.navigateTo(n);
  }

  clearAll(): void {
    this.svc.clearAll();
  }

  iconName(type: DashNotification['type']): string {
    return {
      order_new: 'shopping_bag',
      order_cancelled: 'remove_shopping_cart',
      support_new: 'confirmation_number',
      support_assigned: 'assignment_ind',
      support_escalated: 'warning',
      support_replied: 'chat',
      support_resolved: 'check_circle',
      support_status_changed: 'sync_alt',
      support_priority_changed: 'flag',
      inventory_low_stock: 'inventory_2',
      product_sold_out: 'no_food',
      product_restored: 'restaurant_menu',
    }[type] ?? 'notifications';
  }

  iconClass(type: DashNotification['type']): string {
    return {
      order_new: 'type-order',
      order_cancelled: 'type-cancelled',
      support_new: 'type-new',
      support_assigned: 'type-assigned',
      support_escalated: 'type-escalated',
      support_replied: 'type-replied',
      support_resolved: 'type-resolved',
      support_status_changed: 'type-replied',
      support_priority_changed: 'type-priority',
      inventory_low_stock: 'type-warning',
      product_sold_out: 'type-escalated',
      product_restored: 'type-resolved',
    }[type] ?? '';
  }

  timeAgo(ms: number): string {
    const diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }
}
